import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { CreditCard, Save, KeyRound, Sparkles, Loader2, PiggyBank, Repeat, ArrowRight } from 'lucide-react';
import { useAuth } from '@/lib/auth_context';
import { authApi, subscriptionsApi } from '@/lib/auth_api';
import { userLlmKeysApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1">
        <Label className="text-[12.5px]">{label}</Label>
        {hint && <p className="mt-0.5 text-[10.5px] text-muted-foreground">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

const BYOK_PROVIDERS = [
  { id: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-…' },
  { id: 'openai',    label: 'OpenAI (GPT)',       placeholder: 'sk-…' },
  { id: 'gemini',    label: 'Google (Gemini)',    placeholder: 'AIza…' },
];

function BYOKSavingsTracker() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    userLlmKeysApi.savings().then(setData).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const monthLabel = (() => {
    try {
      const [y, m] = data.month.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long' });
    } catch { return 'this month'; }
  })();

  return (
    <div className="mt-1 flex items-center gap-2.5 rounded-md border border-success/30 bg-success/5 px-3 py-2" data-testid="byok-savings-tracker">
      <PiggyBank className="h-4 w-4 shrink-0 text-success" />
      <div className="min-w-0 flex-1 text-[11px] text-muted-foreground">
        {data.tokens > 0 ? (
          <>
            <span className="font-semibold text-success" data-testid="byok-savings-tokens">
              ~{data.tokens.toLocaleString()} tokens
            </span>{' '}
            (~${data.est_cost_usd.toFixed(2)}) saved in {monthLabel} by using your own key
            instead of your plan&apos;s allowance.
          </>
        ) : (
          <>No BYOK usage yet in {monthLabel} — once a drafting call uses one of your keys, your savings will show up here.</>
        )}
      </div>
    </div>
  );
}

function BillingModeCard() {
  const { user } = useAuth();
  const [plans, setPlans] = React.useState([]);
  const [busy,  setBusy]  = React.useState(false);

  const currentMode = user?.billing_mode || 'standard';
  const tier        = user?.subscription_tier;
  const cadence     = user?.subscription_cadence || 'monthly';

  React.useEffect(() => {
    if (!tier) return;
    subscriptionsApi.plans()
      .then((r) => setPlans(r.plans || []))
      .catch(() => {});
  }, [tier]);

  const plan = plans.find((p) => p.id === tier);
  const planSupportsByok = !!plan?.byok && plan?.byok_monthly_price_usd != null;
  if (!tier || !planSupportsByok) return null;

  const standardPrice = plan?.[cadence]?.price_usd ?? plan?.monthly?.price_usd;
  const byokKey       = (cadence === 'yearly' && plan?.yearly?.price_usd)
    ? 'byok_yearly_price_usd'
    : 'byok_monthly_price_usd';
  const byokPrice     = plan?.[byokKey];
  const displayCadence = (cadence === 'yearly' && plan?.yearly?.price_usd) ? 'yr' : 'mo';
  const savings       = standardPrice && byokPrice ? Math.round((1 - byokPrice / standardPrice) * 100) : 0;

  const switchMode = async (targetMode) => {
    setBusy(true);
    try {
      const r = await subscriptionsApi.checkout(
        tier, cadence, window.location.origin, false, '', targetMode,
      );
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="billing-mode-card">
      <div className="flex items-start gap-3">
        <div className={cn(
          'flex h-8 w-8 items-center justify-center rounded-sm',
          currentMode === 'byok' ? 'bg-amber-100 text-amber-700' : 'bg-primary/15 text-primary',
        )}>
          <Repeat className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Billing mode</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {currentMode === 'byok'
              ? `BYOK — You pay $${byokPrice?.toFixed(2) ?? '?'}/${displayCadence} for the platform; AI calls bill your own key directly.`
              : `Standard — $${standardPrice?.toFixed(2) ?? '?'}/${displayCadence}, all AI usage covered by your token allowance.`
            }
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className={cn(
              'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10.5px] font-semibold',
              currentMode === 'byok'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-primary/10 text-primary',
            )} data-testid="billing-current-mode-pill">
              <span className={cn('h-1.5 w-1.5 rounded-full', currentMode === 'byok' ? 'bg-amber-500' : 'bg-primary')} />
              {currentMode === 'byok' ? 'BYOK active' : 'Standard'}
            </span>
            {currentMode === 'byok' && savings > 0 && (
              <span className="text-[10px] text-muted-foreground">Saving ~{savings}% vs standard</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
        {currentMode === 'standard' && (
          <Button
            size="sm" variant="outline" disabled={busy}
            onClick={() => switchMode('byok')}
            data-testid="billing-switch-byok-btn"
            className="h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            Switch to BYOK — save {savings}%
          </Button>
        )}
        {currentMode === 'byok' && (
          <Button
            size="sm" variant="outline" disabled={busy}
            onClick={() => switchMode('standard')}
            data-testid="billing-switch-standard-btn"
            className="h-8 gap-1.5"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            Switch to Standard
          </Button>
        )}
        <p className="text-[10px] text-muted-foreground">
          Starting a new checkout session — you will not be double-charged; the old subscription is replaced at checkout.
        </p>
      </div>
    </div>
  );
}

function ByokWelcomeBanner() {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    if (localStorage.getItem('byok_welcome_pending') === '1') {
      localStorage.removeItem('byok_welcome_pending');
      setShow(true);
    }
  }, []);

  if (!show) return null;

  return (
    <div
      data-testid="byok-welcome-banner"
      className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3.5"
      role="alert"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <div className="flex-1">
        <p className="text-[12.5px] font-semibold text-amber-900">BYOK mode active — one step left</p>
        <p className="mt-0.5 text-[11.5px] text-amber-800">
          Paste your OpenAI, Anthropic, or Gemini key below. Every draft that uses that
          provider will bill your own account; your platform fee stays fixed.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setShow(false)}
        className="ml-1 mt-0.5 rounded-sm text-amber-500 hover:text-amber-700"
        aria-label="Dismiss"
        data-testid="byok-welcome-banner-dismiss"
      >
        ✕
      </button>
    </div>
  );
}

function BringYourOwnKeyCard() {
  const { user } = useAuth();
  const [status, setStatus] = React.useState(null);
  const [drafts, setDrafts] = React.useState({});
  const [busy,   setBusy]   = React.useState(null);

  const load = React.useCallback(() => {
    userLlmKeysApi.list().then((r) => setStatus(r.providers || [])).catch(() => setStatus([]));
  }, []);
  React.useEffect(() => { if (user?.byok_eligible) load(); }, [user?.byok_eligible, load]);

  if (!user?.byok_eligible) return null;

  const isConfigured = (id) => !!status?.find((s) => s.provider === id)?.configured;

  const save = async (id) => {
    const key = (drafts[id] || '').trim();
    if (!key) return;
    setBusy(id);
    try {
      await userLlmKeysApi.set(id, key);
      setDrafts((d) => ({ ...d, [id]: '' }));
      toast.success(`${id} key saved — your calls now bill your own account.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save key.');
    } finally { setBusy(null); }
  };

  const clear = async (id) => {
    setBusy(id);
    try {
      await userLlmKeysApi.clear(id);
      toast.success(`${id} key cleared.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear key.');
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="billing-byok-card">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Bring your own AI key (BYOK)</div>
          <div className="text-[11px] text-muted-foreground">
            Paste a personal OpenAI, Anthropic, or Gemini key — every drafting call that uses that
            provider bills your own account directly instead of your plan&apos;s token allowance. Keys
            are encrypted and visible only to you. Entirely optional.
          </div>
        </div>
      </div>

      {status === null ? (
        <div className="mt-3 flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading key status…
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {BYOK_PROVIDERS.map((p) => {
            const configured = isConfigured(p.id);
            return (
              <div key={p.id} className="space-y-1.5" data-testid={`byok-row-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11.5px] font-medium">{p.label}</span>
                  <span className="inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] font-medium">
                    <span className={cn('h-1.5 w-1.5 rounded-full', configured ? 'bg-success' : 'bg-muted-foreground/40')} />
                    {configured ? 'Key active' : 'Not configured'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    autoComplete="off"
                    value={drafts[p.id] || ''}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    placeholder={configured ? `${p.placeholder} (replace stored key)` : p.placeholder}
                    className="h-8 flex-1 font-mono-stat text-[11.5px]"
                    data-testid={`byok-input-${p.id}`}
                  />
                  <Button
                    size="sm" className="h-8 gap-1.5" onClick={() => save(p.id)}
                    disabled={busy === p.id || !(drafts[p.id] || '').trim()}
                    data-testid={`byok-save-${p.id}`}
                  >
                    {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </Button>
                  {configured && (
                    <Button
                      size="sm" variant="ghost" className="h-8 gap-1.5 text-muted-foreground"
                      onClick={() => clear(p.id)} disabled={busy === p.id}
                      data-testid={`byok-clear-${p.id}`}
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Clear
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {status?.some((s) => s.configured) && <BYOKSavingsTracker />}
    </div>
  );
}

export function BillingPanel() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [key,  setKey]  = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const hasKey = !!user?.has_personal_stripe_key;

  const save = async () => {
    setBusy(true);
    try {
      await authApi.setKeys({ stripe_api_key: key });
      await refresh?.();
      setKey('');
      toast.success(t('billing.keySaved'));
    } catch { toast.error('Could not save key.'); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await authApi.setKeys({ stripe_api_key: '' });
      await refresh?.();
      toast.success(t('billing.keyCleared'));
    } catch { toast.error('Could not clear key.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary">
            <CreditCard className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-[12.5px] font-semibold">Stripe billing — personal override</div>
            <div className="text-[11px] text-muted-foreground">
              Token packs are processed through Stripe Checkout. App-wide pricing and the default
              Stripe key are managed by the admin (see <span className="font-mono-stat">/admin → API Keys</span>).
              If you want this account&apos;s checkouts to be processed through <b>your own</b> Stripe
              account, paste your Secret Key below — it overrides the admin default for you only.
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-secondary px-2 py-0.5 text-[10.5px] font-medium">
              <span className={cn('h-1.5 w-1.5 rounded-full', hasKey ? 'bg-success' : 'bg-warning')} />
              {hasKey ? 'Personal key active' : 'Using app-wide default key'}
            </div>
          </div>
        </div>
      </div>

      <Row label="Your Stripe Secret Key" hint="Stored encrypted; visible only to you. Starts with sk_test_ or sk_live_.">
        <Input
          type="password"
          autoComplete="off"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={hasKey ? 'sk_live_… (replace stored key)' : 'sk_test_xxxxxxx'}
          className="h-8 w-80 font-mono-stat text-[11.5px]"
        />
      </Row>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={busy || !key.trim()} size="sm" className="gap-1.5">
          <Save className="h-3.5 w-3.5" /> {t('billing.saveKey')}
        </Button>
        {hasKey && (
          <Button onClick={clear} disabled={busy} variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
            <KeyRound className="h-3.5 w-3.5" /> {t('billing.clearKey')}
          </Button>
        )}
      </div>

      <div className="rounded-md border border-border bg-panel-header/30 p-3 text-[10.5px] text-muted-foreground">
        <b className="text-foreground">Pricing reference</b> — Token packs are configured by the admin at <span className="font-mono-stat">/admin → Pricing</span>.
      </div>

      <BillingModeCard />
      <ByokWelcomeBanner />
      <BringYourOwnKeyCard />
    </>
  );
}
